import { useMemo } from "react"
import { Select, type SelectProps } from "@octopus-energy/coral/atoms"
import { type Maybe } from "@oefr/utils/types"
import { useTranslations } from "next-intl"
import { type FragmentOf, graphql, readFragment } from "@/graphql"

const graphQLFragments = {
    meterPoint: graphql(`
        fragment MeterPointSelect_MeterPoint on MeterPointInterface {
            __typename
            id
        }
    `),
}

function getEnergyLabelByTypename(typename: string | undefined) {
    switch (typename) {
        case "GasMeterPoint":
            return "Gaz"
        default:
            return "Electricité"
    }
}

type ReadingsMeterPointSelectProps = {
    meterPoints: Maybe<FragmentOf<typeof graphQLFragments.meterPoint>>[]
    onChange: SelectProps["onChange"]
    currentMeterPointId?: string
}

export function MeterPointSelect({
    meterPoints,
    onChange,
    currentMeterPointId,
}: ReadingsMeterPointSelectProps) {
    const t = useTranslations("ReadingsMeterPointSelect")

    const meterPointSelectOptions = useMemo(
        () =>
            meterPoints
                .map((fragment) => {
                    const meterPoint = readFragment(graphQLFragments.meterPoint, fragment)

                    if (!meterPoint) return null

                    return {
                        label: `${getEnergyLabelByTypename(meterPoint.__typename)} - ${
                            meterPoint.id
                        }`,
                        value: meterPoint.id,
                    }
                })
                .filter(Boolean),
        [meterPoints]
    )

    const currentOption = meterPointSelectOptions.find(
        (option) => option.value === currentMeterPointId
    )

    if (meterPointSelectOptions.length <= 1) {
        return null
    }

    return (
        <Select
            label={t("label")}
            defaultValue={meterPointSelectOptions[0]}
            value={currentOption}
            values={meterPointSelectOptions}
            onChange={onChange}
        />
    )
}

MeterPointSelect.messages = ["ReadingsMeterPointSelect"] as const
MeterPointSelect.fragments = graphQLFragments
